import React, { useState, useEffect, useRef } from "react";
import { User, Lock, Loader2, Gift, Phone, ArrowLeft, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";
import { authApi } from "../lib/api";
import { auth } from "../lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";


// Stable per-browser id used server-side to cap accounts created from one device.
const getDeviceId = () => {
  try {
    let id = localStorage.getItem("bbw_device");
    if (!id) { id = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36)); localStorage.setItem("bbw_device", id); }
    return id;
  } catch { return ""; }
};

export default function AuthModal({ open, onOpenChange, initialView = "login" }) {
  const { login } = useApp();
  const { toast } = useToast();
  const [view, setView] = useState("login"); // login | register | register-otp | forgot | forgot-otp
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [affiliateCode, setAffiliateCode] = useState("");
  const [otp, setOtp] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  // Prefill the referral code from a shared link (?ref=CODE) so a friend who
  // opens the referral URL gets the code applied automatically.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) setReferralCode(ref.toUpperCase());
      const aff = params.get("aff");
      if (aff) setAffiliateCode(aff.toUpperCase());
    } catch { /* no-op */ }
  }, []);

  // When the modal opens, start on the requested view (login or register).
  useEffect(() => {
    if (open) setView(initialView);
  }, [open, initialView]);

  const getRecaptcha = () => {
    if (recaptchaRef.current) {
      try { recaptchaRef.current.clear(); } catch (e) { console.debug("recaptcha clear skipped:", e?.message); }
      recaptchaRef.current = null;
    }
    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
    return recaptchaRef.current;
  };

  const sendFirebaseOtp = async (e164) => {
    const verifier = getRecaptcha();
    confirmationRef.current = await signInWithPhoneNumber(auth, e164, verifier);
  };

  const fbErr = (e, fallback) => {
    const map = {
      "auth/invalid-phone-number": "Enter a valid mobile number.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/invalid-verification-code": "Incorrect OTP. Please check and retry.",
      "auth/code-expired": "OTP expired. Tap Resend to get a new code.",
      "auth/quota-exceeded": "SMS limit reached. Try again later.",
    };
    toast({ title: "Error", description: map[e?.code] || e?.message || fallback });
  };

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const resetAll = () => {
    setView("login"); setName(""); setUsername(""); setPassword("");
    setPhone(""); setReferralCode(""); setOtp(""); setMaskedPhone("");
    setResendIn(0);
  };

  useEffect(() => { if (!open) resetAll(); }, [open]);

  const err = (e, fallback) => toast({ title: "Error", description: e?.response?.data?.detail || fallback });

  const doLogin = async () => {
    if (username.trim().length < 3 || password.length < 4) {
      toast({ title: "Enter username and password" }); return;
    }
    setBusy(true);
    try {
      const res = await authApi.login({ username: username.trim(), password });
      login({ token: res.token, user: res.user });
      toast({ title: "Welcome back! 🏏", description: `Signed in as ${res.user.username}` });
      onOpenChange(false);
    } catch (e) { err(e, "Login failed"); } finally { setBusy(false); }
  };

  const sendRegisterOtp = async () => {
    if (username.trim().length < 3) { toast({ title: "Username must be at least 3 characters" }); return; }
    if (password.length < 6) { toast({ title: "Password must be at least 6 characters" }); return; }
    if (!/^[6-9]\d{9}$/.test(phone)) { toast({ title: "Invalid mobile number", description: "Enter a 10-digit number starting with 6, 7, 8 or 9." }); return; }
    setBusy(true);
    try {
      await sendFirebaseOtp(`+91${phone}`);
      setOtp("");
      setView("register-otp");
      setResendIn(100);
      toast({ title: "OTP sent ✅", description: "Check your phone for the verification code." });
    } catch (e) { fbErr(e, "Could not send OTP"); } finally { setBusy(false); }
  };

  const verifyRegister = async () => {
    if (!confirmationRef.current) { toast({ title: "Please request an OTP first" }); return; }
    setBusy(true);
    try {
      const cred = await confirmationRef.current.confirm(otp);
      const firebaseToken = await cred.user.getIdToken();
      const res = await authApi.register({
        name: name.trim(), username: username.trim(), password, phone: `+91${phone}`, firebaseToken, referralCode, affiliateCode,
        deviceId: getDeviceId(),
      });
      login({ token: res.token, user: res.user }, { newSignup: true });
      toast({ title: "Welcome to BatBallWorld! 🏏", description: `Account created for ${res.user.username}` });
      onOpenChange(false);
    } catch (e) {
      if (e?.code) fbErr(e, "Verification failed"); else err(e, "Sign up failed");
    } finally { setBusy(false); }
  };

  const sendForgotOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(phone)) { toast({ title: "Invalid mobile number", description: "Enter your registered 10-digit mobile number." }); return; }
    setBusy(true);
    try {
      await sendFirebaseOtp(`+91${phone}`);
      setOtp("");
      setMaskedPhone(`+91 ${phone}`);
      setView("forgot-otp");
      setResendIn(100);
      toast({ title: "OTP sent ✅", description: "Check your phone for the verification code." });
    } catch (e) { fbErr(e, "Could not send OTP"); } finally { setBusy(false); }
  };

  const doReset = async () => {
    if (password.length < 6) { toast({ title: "New password must be at least 6 characters" }); return; }
    if (!confirmationRef.current) { toast({ title: "Please request an OTP first" }); return; }
    setBusy(true);
    try {
      const cred = await confirmationRef.current.confirm(otp);
      const firebaseToken = await cred.user.getIdToken();
      await authApi.resetPassword({ firebaseToken, password });
      toast({ title: "Password reset ✅", description: "Sign in with your new password." });
      setPassword(""); setOtp(""); setView("login");
    } catch (e) {
      if (e?.code) fbErr(e, "Verification failed"); else err(e, "Reset failed");
    } finally { setBusy(false); }
  };

  const FIELD = "flex items-center gap-2 rounded-md bg-white border border-slate-300 px-3 text-slate-900 focus-within:ring-2 focus-within:ring-[#f5b400]";
  const PhoneField = (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs">Mobile number</Label>
      <div className={FIELD}>
        <span className="text-slate-500 text-sm flex items-center gap-1"><Phone className="h-4 w-4" />+91</span>
        <input className="flex-1 bg-transparent py-3 outline-none text-[15px] placeholder:text-slate-400" placeholder="98765 43210"
          inputMode="numeric" maxLength={10} value={phone}
          onChange={(e) => {
            let v = e.target.value.replace(/\D/g, "");
            if (v && !"6789".includes(v[0])) v = "";
            setPhone(v.slice(0, 10));
          }} />
      </div>
    </div>
  );

  const UsernameField = (dis = false, label = true) => (
    <div className="space-y-1.5">
      {label && <Label className="text-slate-300 text-xs">Username</Label>}
      <div className={FIELD}>
        <User className="h-4 w-4 text-slate-500" />
        <input className="flex-1 bg-transparent py-3 outline-none text-[15px] placeholder:text-slate-400 disabled:opacity-60" placeholder={label ? "Choose a username" : "Username"}
          value={username} disabled={dis} autoCapitalize="none"
          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))} />
      </div>
    </div>
  );

  const PasswordField = (label, placeholder, showLabel = true) => (
    <div className="space-y-1.5">
      {showLabel && <Label className="text-slate-300 text-xs">{label}</Label>}
      <div className={FIELD}>
        <Lock className="h-4 w-4 text-slate-500" />
        <input type="password" className="flex-1 bg-transparent py-3 outline-none text-[15px] placeholder:text-slate-400" placeholder={placeholder}
          value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && view === "login") doLogin(); }} />
      </div>
    </div>
  );

  const OtpBlock = (
    <div className="flex justify-center">
      <InputOTP maxLength={6} value={otp} onChange={setOtp}>
        <InputOTPGroup>{[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
      </InputOTP>
    </div>
  );

  const ResendRow = ({ onResend }) => (
    <div className="text-center text-sm" data-testid="otp-resend-row">
      {resendIn > 0 ? (
        <span className="text-slate-400">Resend code in <span className="text-white font-semibold tabular-nums" data-testid="otp-resend-timer">{resendIn}s</span></span>
      ) : (
        <button className="text-[#f5b400] font-semibold disabled:opacity-60" onClick={onResend} disabled={busy} data-testid="otp-resend-btn">
          Didn't get it? Resend OTP
        </button>
      )}
    </div>
  );

  const titles = {
    login: "Login",
    register: "Create your account",
    "register-otp": "Verify your number",
    forgot: "Forgot password",
    "forgot-otp": "Reset password",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen max-w-[100vw] h-[100dvh] rounded-none sm:w-full sm:max-w-sm sm:h-auto sm:rounded-xl p-0 gap-0 grid-rows-[auto_1fr] grid-cols-[minmax(0,1fr)] overflow-x-hidden [&>*]:min-w-0 bg-[#1a1a1a] border-0 text-white overflow-y-auto [&>button]:right-4 [&>button]:top-4 [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-full [&>button]:bg-black/70 [&>button]:opacity-100 [&>button]:grid [&>button]:place-items-center [&>button]:text-white" data-testid="auth-modal">
        <div className="bg-[#f5b400] pb-[3px]" style={{ borderRadius: "0 0 50% 50% / 0 0 44px 44px" }}>
          <div className="relative h-[230px] overflow-hidden bg-black" style={{ borderRadius: "0 0 50% 50% / 0 0 42px 42px" }}>
            <img src="/img/sports.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/20" />
            <div className="absolute inset-x-0 bottom-6 flex flex-col items-center gap-1.5">
              <img src="/img/logo.webp" alt="BatBallWorld" className="h-[84px] w-[84px] drop-shadow-[0_0_18px_rgba(245,180,0,0.6)]" />
              <div className="flex flex-col items-center leading-none">
                <span className="font-display font-extrabold text-2xl tracking-wide text-[#f5b400]">BATBALL</span>
                <span className="font-display font-bold text-[11px] tracking-[0.35em] text-white/90">WORLD</span>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pt-6 pb-8">
        <DialogHeader className={view === "login" ? "sr-only" : "mb-3"}>
          <DialogTitle className="font-display text-lg flex items-center gap-2 text-white">
            {(view === "register-otp" || view === "forgot" || view === "forgot-otp") && (
              <button onClick={() => setView(view === "register-otp" ? "register" : "login")} className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            {titles[view]}
          </DialogTitle>
        </DialogHeader>

        {/* LOGIN */}
        {view === "login" && (
          <div className="space-y-3" data-testid="login-view">
            {UsernameField(false, false)}
            {PasswordField("Password", "Password", false)}
            <Button
              className="w-full h-11 rounded-md font-semibold text-[15px] text-white border-0 bg-[#111111] hover:bg-black shadow-md"
              onClick={doLogin}
              disabled={busy}
              data-testid="login-submit-btn"
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Logging in…</> : "Login"}
            </Button>
            <div className="text-center text-sm text-slate-200 pt-1">
              Don't have an account?{" "}
              <button className="text-[#f5b400] font-semibold" onClick={() => { resetAll(); setView("register"); }} data-testid="login-signup-link">Sign Up</button>
            </div>
            <div className="text-center text-sm">
              <button className="text-white/90 font-medium" onClick={() => { setView("forgot"); setPassword(""); }} data-testid="login-forgot-link">Forgot Password ?</button>
            </div>
          </div>
        )}

        {/* REGISTER form */}
        {view === "register" && (
          <div className="space-y-2.5 pt-0.5">
            {UsernameField()}
            {PasswordField("Password", "Create a password")}
            {PhoneField}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="flex items-center gap-1 text-xs text-slate-300"><Gift className="h-3 w-3 text-fuchsia-400" /> Referral <span className="text-slate-500 font-normal">(opt.)</span></Label>
                <Input placeholder="₹100 bonus" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} className="uppercase h-10 text-sm bg-white text-slate-900 border-slate-300" />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1 text-xs text-slate-300"><Users className="h-3 w-3 text-teal-400" /> Affiliate <span className="text-slate-500 font-normal">(opt.)</span></Label>
                <Input placeholder="Code" value={affiliateCode} onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())} className="uppercase h-10 text-sm bg-white text-slate-900 border-slate-300" data-testid="signup-affiliate-input" />
              </div>
            </div>
            <Button className="w-full h-11 rounded-md font-semibold text-white border-0 bg-[#111111] hover:bg-black" onClick={sendRegisterOtp} disabled={busy}>
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending OTP…</> : "Send OTP to verify"}
            </Button>
            <div className="text-center text-sm text-slate-200">
              Have an account?{" "}
              <button className="text-[#f5b400] font-semibold" onClick={() => { resetAll(); }}>Login</button>
            </div>
          </div>
        )}

        {/* REGISTER OTP */}
        {view === "register-otp" && (
          <div className="space-y-5 pt-2">
            <div className="text-sm text-slate-300">Enter the 6-digit code sent to <span className="text-white font-medium">+91 {phone}</span></div>
            {OtpBlock}
            <Button className="w-full h-11 rounded-md font-semibold text-white border-0 bg-[#111111] hover:bg-black" onClick={verifyRegister} disabled={otp.length < 6 || busy}>
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating account…</> : "Verify & Create account"}
            </Button>
            <ResendRow onResend={sendRegisterOtp} />
          </div>
        )}

        {/* FORGOT: enter phone */}
        {view === "forgot" && (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-300">Enter your registered mobile number and we'll send an OTP to verify it's you.</p>
            {PhoneField}
            <Button className="w-full h-11 rounded-md font-semibold text-white border-0 bg-[#111111] hover:bg-black" onClick={sendForgotOtp} disabled={busy}>
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending OTP…</> : "Send OTP"}
            </Button>
          </div>
        )}

        {/* FORGOT: OTP + new password */}
        {view === "forgot-otp" && (
          <div className="space-y-5 pt-2">
            <div className="text-sm text-slate-300">Enter the OTP sent to <span className="text-white font-medium">{maskedPhone}</span> and set a new password.</div>
            {OtpBlock}
            {PasswordField("New password", "Enter new password")}
            <Button className="w-full h-11 rounded-md font-semibold text-white border-0 bg-[#111111] hover:bg-black" onClick={doReset} disabled={otp.length < 6 || busy}>
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting…</> : "Reset password"}
            </Button>
            <ResendRow onResend={sendForgotOtp} />
          </div>
        )}
        <div id="recaptcha-container" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
